// FeedbackHost — Help: the guided tour and "Give feedback" in one place (pen
// Q68), and "Report this" (#84, pen I-06, I-08).
//
// The provider wraps the shell: it holds the feedback draft (view-ephemeral:
// un-submitted text, the kind, the chips removed) and what the Help tab has
// open, and is the door every error box's "Report this" goes through
// (`ReportProvider`). `HelpEntry` renders the Help tab on the page's bottom
// edge (ChromeShellFrame) and, above it, the Help menu or the feedback panel:
// the menu's Give feedback gives way to the panel in the same spot.
//
// Send is the one command (`sendFeedbackReport`). It worked: the draft
// empties, the panel closes and a quiet toast thanks the person. It failed:
// everything written stays and the panel says why.

export { FeedbackHost, HelpEntry };

import { exhaustSwitchCase, Result } from "@bilda/utils";
import { useLingui } from "@lingui/react/macro";
import { createContext, type ReactNode, useContext, useState } from "react";
import {
    FeedbackPanel,
    type FeedbackSendStatus,
} from "../../../components/Feedback/FeedbackPanel.js";
import { HelpMenu, HelpTab } from "../../../components/HelpTab/HelpTab.js";
import type { ActiveTab } from "../../../components/Navigation/types.js";
import { Popover } from "../../../components/Popover/Popover.js";
import {
    type ClientFacts,
    canSend,
    contextOf,
    draftForError,
    emptyDraft,
    type FeedbackDraft,
    type Report,
    ReportProvider,
    reportOf,
    sendFailureOf,
} from "../../feedback.js";
import { useAppShell } from "../context.js";

// What the shell read once about the build and the browser.
type BuildFacts = Omit<ClientFacts, "view">;

// What the Help tab has open above it: nothing, its menu, or the feedback
// panel (from the menu, or from an error box's "Report this").
type HelpOpen = "closed" | "menu" | "feedback";

type FeedbackModel = {
    readonly open: HelpOpen;
    readonly setOpen: (open: HelpOpen) => void;
    readonly draft: FeedbackDraft;
    readonly setDraft: (update: (d: FeedbackDraft) => FeedbackDraft) => void;
    readonly status: FeedbackSendStatus;
    readonly send: () => Promise<void>;
    readonly facts: ClientFacts;
};

const FeedbackContext = createContext<FeedbackModel | null>(null);

// The view the person is on, as the menu names it.
const useViewName = (): ((active: ActiveTab) => string) => {
    const { t } = useLingui();
    return (active) => {
        switch (active.type) {
            case "resource":
                return t`Material`;
            case "conversation":
                return t`Chat`;
            case "settings":
                return t`Settings`;
            case "home":
                return t`Home`;
            case "studio":
                return t`Studio`;
            case "projects":
                return t`Projects`;
            case "knowledge":
                return t`Uploaded sources`;
            case "search":
                return t`Search`;
            case "quality":
                return t`Quality report`;
            default:
                return exhaustSwitchCase(active);
        }
    };
};

const FeedbackHost = ({
    build,
    children,
}: {
    readonly build: BuildFacts;
    readonly children: ReactNode;
}): ReactNode => {
    const { ports, showNotice, tabs } = useAppShell();
    const viewName = useViewName();
    const [open, setOpen] = useState<HelpOpen>("closed");
    const [draft, setDraftState] = useState<FeedbackDraft>(emptyDraft);
    const [status, setStatus] = useState<FeedbackSendStatus>({ type: "idle" });
    const facts: ClientFacts = { ...build, view: viewName(tabs.active) };

    const setDraft = (update: (d: FeedbackDraft) => FeedbackDraft) => {
        setDraftState(update);
        if (status.type === "failed") setStatus({ type: "idle" });
    };

    const report: Report = (request) => {
        setDraftState((d) => draftForError(d, request));
        setStatus({ type: "idle" });
        setOpen("feedback");
    };

    const send = async () => {
        setStatus({ type: "sending" });
        const r = await ports.sendFeedbackReport(reportOf(draft, facts));
        if (Result.isErr(r)) {
            setStatus({ type: "failed", reason: sendFailureOf(r.error) });
            return;
        }
        setDraftState(emptyDraft);
        setStatus({ type: "idle" });
        setOpen("closed");
        showNotice({ type: "feedback-sent" });
    };

    return (
        <FeedbackContext.Provider
            value={{ open, setOpen, draft, setDraft, status, send, facts }}
        >
            <ReportProvider value={report}>{children}</ReportProvider>
        </FeedbackContext.Provider>
    );
};

// The Help tab and what it opens. Nothing outside a FeedbackHost. `look`:
// the tab on the page's bottom edge, or the quiet row at the foot of the
// Studio's outline (pen Q76), which opens the same menu beside it.
const HelpEntry = ({ look }: { readonly look: "tab" | "row" }): ReactNode => {
    const { t } = useLingui();
    const { coach } = useAppShell();
    const model = useContext(FeedbackContext);
    if (model === null) return null;
    const { open, setOpen, draft, setDraft, status, send, facts } = model;
    const tour = coach.viewTour;
    return (
        <Popover
            trigger={<HelpTab look={look} open={open !== "closed"} />}
            label={
                open !== "feedback"
                    ? t`Help and feedback`
                    : draft.error === null
                      ? t`Give feedback`
                      : t`Report a problem`
            }
            side={look === "tab" ? "top" : "right"}
            align="end"
            look="bare"
            open={open !== "closed"}
            onOpenChange={(next) => setOpen(next ? "menu" : "closed")}
        >
            {open === "feedback" ? (
                <FeedbackPanel
                    fromError={draft.error !== null}
                    kind={draft.kind}
                    onKind={(kind) => setDraft((d) => ({ ...d, kind }))}
                    text={draft.text}
                    onText={(text) => setDraft((d) => ({ ...d, text }))}
                    chips={contextOf(draft, facts)}
                    onRemoveChip={(chip) =>
                        setDraft((d) => ({
                            ...d,
                            removed: new Set([...d.removed, chip]),
                        }))
                    }
                    mayContact={draft.mayContact}
                    onMayContact={(mayContact) =>
                        setDraft((d) => ({ ...d, mayContact }))
                    }
                    canSend={canSend(draft, facts)}
                    status={status}
                    onSend={send}
                    onClose={() => setOpen("closed")}
                />
            ) : (
                <HelpMenu
                    tour={tour}
                    onTour={() => {
                        setOpen("closed");
                        tour?.start();
                    }}
                    onFeedback={() => setOpen("feedback")}
                />
            )}
        </Popover>
    );
};
